import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef
} from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LoadingButton from "@mui/lab/LoadingButton";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { useDebounce } from "hooks";
import { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import AutocompleteFormField from "components/autocomplete-form-field";
import CheckboxFormField from "components/checkbox-form-field";
import Dialog from "components/dialog";
import TypingIndicatorV2 from "components/typing-indicator-v2.component";
import { selectCurrentUser } from "features/auth/auth-slice";
import { selectCompletionChunkPreview } from "features/notifications/notifications.slice";
import { useGetLastPromptsQuery } from "services/prompts";
import type { Prompt } from "services/types/prompts.type";
import { convertHtmlToText, sanitizeHTML } from "utils/common";

import {
  selectAiPromptById,
  setAiPromptDetails
} from "../word-processor.slice";
import TextSkeleton from "./text-skeleton.component";

type AiPromptDialogProps = {
  id: string;
  editorRef: React.MutableRefObject<Editor | null>;
  onAskAi?: (params: {
    id: string;
    prompt: string;
    fieldContent: string;
    lastResult?: string;
  }) => void;
  handleChangeValue: (html?: string | undefined, editor?: Editor) => void;
  promptType?: number;
};

const DEFAULT_VALUES = {
  prompt: "",
  needReplace: false
};

export default function AiPromptDialog({
  id,
  editorRef,
  onAskAi,
  handleChangeValue,
  promptType
}: AiPromptDialogProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const { zIndex } = useTheme();
  const dispatch = useAppDispatch();
  const aiPrompt = useAppSelector((state) => selectAiPromptById(state, id));

  const defaultValues = useMemo(
    () => ({
      ...DEFAULT_VALUES,
      prompt: aiPrompt.prompt ?? ""
    }),
    [aiPrompt.prompt]
  );

  const formMethods = useForm<{
    prompt: string;
    needReplace: boolean;
  }>({
    mode: "onChange",
    defaultValues
  });

  const { handleSubmit, control, reset, watch } = formMethods;

  const prompt = watch("prompt");

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleClose = useCallback(() => {
    dispatch(setAiPromptDetails({ id, prompt: "", dialogOpen: false }));
  }, [dispatch, id]);

  const isBusy = aiPrompt?.isBusy;

  const onSubmit = useCallback(
    ({ needReplace }: { prompt: string; needReplace: boolean }) => {
      // Sanitize on Insert: the editable Box trusts local edits during typing
      // (see handleResultChange below), so this is the chokepoint where any
      // pasted-from-clipboard HTML is run through the allowlist before it
      // reaches TinyMCE.
      const html = sanitizeHTML(aiPrompt?.result ?? "");

      if (needReplace) {
        editorRef.current?.setContent(html);
      } else {
        editorRef.current?.insertContent(html);
      }
      handleChangeValue(
        editorRef.current?.getContent(),
        editorRef.current as Editor
      );
      handleClose();
    },
    [aiPrompt?.result, editorRef, handleChangeValue, handleClose]
  );

  const textContent = useMemo(
    () => convertHtmlToText(aiPrompt?.result),
    [aiPrompt?.result]
  );

  // Refs that drive the contentEditable result Box.
  // - resultRef points at the editable DOM node so we can set innerHTML once
  //   per external update without React re-rendering children (which would
  //   wipe the user's caret position).
  // - lastSeenResultRef tracks the source value (aiPrompt.result) the
  //   component last processed. We compare on the SOURCE value, not the
  //   sanitizer output, so browser-canonicalized innerHTML from local edits
  //   stays in sync without round-tripping through the sanitizer (which has
  //   a different canonical form and would loop on every keystroke).
  const resultRef = useRef<HTMLDivElement | null>(null);
  const lastSeenResultRef = useRef<string>("");

  // Callback ref handles the mount-time sync: when the contentEditable Box
  // first attaches (e.g., after isBusy flips false on the initial AI result),
  // we write the current result into the DOM immediately, with no dependency
  // on effect scheduling. This is the belt-and-suspenders coverage for the
  // case where the result value preceded the contentEditable ever mounting.
  const setResultNode = useCallback(
    (node: HTMLDivElement | null) => {
      resultRef.current = node;
      if (!node) return;
      const incoming = aiPrompt?.result ?? "";
      if (incoming === lastSeenResultRef.current) return;
      lastSeenResultRef.current = incoming;
      node.innerHTML = sanitizeHTML(incoming);
    },
    [aiPrompt?.result]
  );

  // useLayoutEffect handles in-place updates while the node stays mounted
  // (e.g., re-prompting without unmounting the box). Synchronous before paint
  // so the user never sees a stale frame.
  useLayoutEffect(() => {
    if (isBusy || !resultRef.current) return;
    const incoming = aiPrompt?.result ?? "";
    // Local-edit dispatch: we already set lastSeenResultRef in the input
    // handler before dispatching, so this branch is a no-op and the caret
    // stays put.
    if (incoming === lastSeenResultRef.current) return;
    lastSeenResultRef.current = incoming;
    resultRef.current.innerHTML = sanitizeHTML(incoming);
  }, [aiPrompt?.result, isBusy]);

  const handleResultChange = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      // Do NOT sanitize here — DOMParser is expensive on every keystroke and
      // mutating innerHTML under the user's caret would jump it. Local edits
      // in the user's own browser session are trusted; sanitization happens
      // on Insert (see onSubmit above).
      const html = event.currentTarget.innerHTML;
      // Mark this value as "seen" BEFORE dispatching so the sync effect
      // skips it when Redux updates aiPrompt.result.
      lastSeenResultRef.current = html;
      dispatch(setAiPromptDetails({ id, result: html }));
    },
    [dispatch, id]
  );

  const handleUpdatePrompt = useCallback(() => {
    if (!prompt) {
      return;
    }
    onAskAi?.({
      id,
      prompt,
      fieldContent: textContent,
      lastResult: textContent
    });
  }, [prompt, onAskAi, id, textContent]);

  const previewText = useSelector(selectCompletionChunkPreview);

  const { value: dSearch } = useDebounce(aiPrompt?.prompt ?? "");

  const { data: lastPrompts = [] } = useGetLastPromptsQuery(
    {
      user: currentUser?.id,
      type: Number(promptType),
      term: dSearch
    },
    {
      skip:
        !currentUser?.id || currentUser?.id === -1 || promptType === undefined,
      refetchOnMountOrArgChange: true
    }
  );

  const filterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option: Prompt) => option.prompt
      }),
    []
  );

  const handleDialogClose = useCallback(
    (_event: unknown, reason: string) => {
      if (reason === "escapeKeyDown") return;
      handleClose();
    },
    [handleClose]
  );

  const handleInsert = useCallback(() => {
    handleSubmit(onSubmit)();
    reset(defaultValues);
  }, [handleSubmit, onSubmit, reset, defaultValues]);

  return (
    <Dialog
      title="Ask AI"
      onClose={handleDialogClose}
      minWidth={1455}
      open
      maxWidth="lg"
      dialogTitleSx={{ pl: "26px" }}
      sx={{ maxHeight: "80%", zIndex: zIndex.snackbar + 1 }}
      dialogContentProps={{
        sx: {
          pb: 2,
          pr: 2,
          pl: 2,
          background: neutral[50]
        }
      }}
    >
      <Stack
        sx={{
          width: "100%",
          minHeight: 70,
          justifyContent: "space-between",
          mt: -1
        }}
      >
        <Stack height="100%" rowGap={3} mb={1}>
          <Stack direction="row" alignItems="flex-end" columnGap={2}>
            <AutocompleteFormField
              name="prompt"
              label="Prompt"
              disabled={isBusy}
              options={lastPrompts}
              isLoading={isBusy}
              getOptionLabel={(option: Prompt | string) =>
                typeof option === "string" ? option : (option?.prompt ?? "")
              }
              filterOptions={filterOptions}
              freeSolo
              renderOption={(props, option) => {
                return (
                  <ListItem
                    {...props}
                    key={typeof option === "string" ? option : option.id}
                  >
                    <Typography variant="body2">
                      {typeof option === "string" ? option : option.prompt}
                    </Typography>
                  </ListItem>
                );
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === "string" || typeof value === "string") {
                  return false;
                }
                return option?.id === value?.id;
              }}
              popupIcon={<ArrowDropDownIcon />}
              control={control}
              autoFocus
              sx={{ ml: 0 }}
              onSubmit={handleUpdatePrompt}
            />
            <LoadingButton
              variant="contained"
              size="small"
              disabled={!prompt}
              loading={isBusy}
              onClick={handleUpdatePrompt}
              startIcon={
                <AutoAwesomeIcon
                  sx={{
                    fontSize: "18px",
                    color: "white"
                  }}
                />
              }
            >
              Update
            </LoadingButton>
          </Stack>

          <Box>
            <Stack direction="row" columnGap={2}>
              <Typography
                variant="body1"
                letterSpacing="0.7px"
                textTransform="uppercase"
                fontWeight={700}
                color={grey[900]}
              >
                AI Response
              </Typography>
              {isBusy && <TypingIndicatorV2 sx={{ pt: 0.8 }} />}
            </Stack>
            {isBusy ? (
              <Box
                position="relative"
                width="100%"
                height={390}
                mt={1}
                p={0.5}
                px={1.5}
                sx={{
                  boxSizing: "border-box",
                  border: `solid 1px ${grey[400]}`,
                  borderRadius: "4px",
                  overflow: "auto"
                }}
              >
                <Typography variant="body2">
                  <Box
                    component="span"
                    mr={1}
                    dangerouslySetInnerHTML={{
                      __html: previewText
                    }}
                  />
                  <TextSkeleton />
                </Typography>
              </Box>
            ) : (
              <Box
                ref={setResultNode}
                contentEditable
                suppressContentEditableWarning
                onInput={handleResultChange}
                position="relative"
                width="100%"
                height={390}
                mt={1}
                p={0.5}
                px={1.5}
                sx={{
                  boxSizing: "border-box",
                  border: `solid 1px ${grey[400]}`,
                  borderRadius: "4px",
                  overflow: "auto",
                  outline: "none",
                  "&:focus": { borderColor: "primary.main" }
                }}
              />
            )}
          </Box>
        </Stack>
        <DialogActions
          sx={{
            position: "sticky",
            bottom: -18,
            background: neutral[50],
            pr: 3
          }}
        >
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Button
              variant="outlined"
              size="small"
              sx={{ mr: -3 }}
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Stack direction="row" columnGap={2} sx={{ mr: -3 }}>
              <FormControlLabel
                label="Replace existing content"
                control={
                  <CheckboxFormField
                    name="needReplace"
                    control={control}
                    testId="need-replace"
                  />
                }
              />

              <LoadingButton
                size="small"
                variant="contained"
                onClick={handleInsert}
                loading={isBusy}
                disabled={!aiPrompt.result}
              >
                Insert
              </LoadingButton>
            </Stack>
          </Stack>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
